import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trash2 } from 'lucide-react';

const AccountDeletion = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="font-heading text-xl font-bold text-foreground">Fencepost</h1>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <Trash2 className="w-8 h-8 text-primary" />
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Account and Data Deletion
          </h1>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">Introduction</h2>
            <p className="text-muted-foreground">
              At Fencepost, we respect your right to control your personal information. 
              You may request deletion of your account and all associated data at any time. 
              This page explains how to make a deletion request and what happens when you do.
            </p>
          </section>

          {/* How to Request */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">How to Request Account Deletion</h2>
            <p className="text-muted-foreground">
              To request deletion of your Fencepost account and data:
            </p>
            <ol className="list-decimal list-inside text-muted-foreground space-y-2 ml-4">
              <li>Send an email to <a href="mailto:support@myfencepost.com" className="text-primary hover:underline">support@myfencepost.com</a></li>
              <li>Use the email address associated with your Fencepost account</li>
              <li>Use the subject line: <strong>"Account Deletion Request"</strong></li>
              <li>In the body, confirm that you want your account and all data permanently deleted</li>
            </ol>
            <p className="text-muted-foreground mt-4">
              We will verify your identity by confirming the request was sent from your registered email address.
            </p>
          </section>

          {/* Data That Will Be Deleted */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">Data That Will Be Deleted</h2>
            <p className="text-muted-foreground">
              When your deletion request is processed, the following data will be permanently removed:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Your user account and login credentials</li>
              <li>Farm and garden records</li>
              <li>Flock and livestock tracking data</li>
              <li>Weather location and alert preferences</li>
              <li>Task lists and daily logs</li>
              <li>Notes and notebook entries</li>
              <li>Photo uploads and diagnosis history</li>
              <li>Saved preferences and settings</li>
            </ul>
          </section>

          {/* Processing Time */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">Processing Time</h2>
            <p className="text-muted-foreground">
              Account deletion requests are processed within <strong>30 days</strong> of verification. 
              You will receive an email confirmation once your account and data have been deleted.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">Data Retention</h2>
            <p className="text-muted-foreground">
              In some cases, we may retain minimal records temporarily where required for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Legal compliance</li>
              <li>Security and fraud prevention</li>
              <li>Financial and accounting records</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Any retained data is not used for marketing or analytics purposes and is deleted 
              as soon as the retention requirement no longer applies.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-xl font-semibold text-foreground">Contact Information</h2>
            <p className="text-muted-foreground">
              If you have questions about account deletion or your data, please contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              <strong>Email:</strong>{' '}
              <a href="mailto:support@myfencepost.com" className="text-primary hover:underline">
                support@myfencepost.com
              </a>
            </p>
          </section>

          {/* Last Updated */}
          <section className="pt-8 border-t border-border/60">
            <p className="text-sm text-muted-foreground">
              Last updated: February 2026
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-card/30 mt-12">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 Fencepost. All rights reserved.
            </p>
            <div className="flex gap-4 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AccountDeletion;
